import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Collapse,
  Tooltip,
  IconButton,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import {
  Search,
  People,
  PersonAdd,
  Repeat,
  ExpandMore,
  ExpandLess,
  LocationCity,
  BookOnline,
  Refresh,
} from "@mui/icons-material";
import { getRequest, server } from "../config";

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color, bg }) => (
  <Card sx={{ flex: 1, minWidth: 150, border: `1.5px solid ${color}30` }}>
    <CardContent sx={{ textAlign: "center", py: 2, px: 1.5 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          backgroundColor: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 1,
        }}
      >
        <Box sx={{ color }}>{icon}</Box>
      </Box>
      <Typography variant="h5" fontWeight="bold" sx={{ color }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
        {label}
      </Typography>
    </CardContent>
  </Card>
);

// ── City Row ─────────────────────────────────────────────────────────────────
const CityRow = ({ city }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    if (data) { setOpen((p) => !p); return; }
    setLoading(true);
    setOpen(true);
    try {
      const res = await getRequest(
        `${server.uri}api/city-analytics?city=${encodeURIComponent(city.name)}`
      );
      const cityData = Array.isArray(res) && res.length > 0 ? res[0] : null;
      setData(cityData);
    } catch (err) {
      console.error("City analytics fetch error:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (open && data) { setOpen(false); } else { fetchData(); }
  };

  return (
    <>
      <TableRow
        hover
        onClick={handleToggle}
        sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f0f4ff" } }}
      >
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationCity fontSize="small" color="primary" />
            <Typography fontWeight="medium">{city.name}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip label={`${(city.pincodes || []).length} pincodes`} size="small" variant="outlined" />
        </TableCell>
        <TableCell align="right">
          <Tooltip title={open ? "Collapse" : "View Analytics"}>
            <IconButton size="small" color="primary">
              {open ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, border: open ? undefined : "none", backgroundColor: "#fafbff" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2.5, px: 2 }}>
              {loading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} />
                </Box>
              ) : data ? (
                <>
                  {/* Partners Section */}
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 1 }}>
                    Partners
                  </Typography>
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard
                        icon={<People />}
                        label="Active Partners Right Now"
                        value={data.activePartners}
                        color="#1565c0"
                        bg="#e3f2fd"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard
                        icon={<People />}
                        label="Total Partners in City"
                        value={data.totalPartners}
                        color="#6a1b9a"
                        bg="#f3e5f5"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard
                        icon={<PersonAdd />}
                        label="New Partners (Last 30 Days)"
                        value={data.newPartnersMonth}
                        color="#2e7d32"
                        bg="#e8f5e9"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Bookings Section */}
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 1 }}>
                    Bookings &amp; Repeat Customers
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard
                        icon={<BookOnline />}
                        label="Bookings (Last 30 Days)"
                        value={data.totalBookingsMonth}
                        color="#e65100"
                        bg="#fff3e0"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard
                        icon={<Repeat />}
                        label="Repeat Customers (Last 30 Days)"
                        value={data.repeatCustomers}
                        color="#c62828"
                        bg="#ffebee"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard
                        icon={<BookOnline />}
                        label="Total Bookings (All Time)"
                        value={data.totalBookingsAllTime}
                        color="#00695c"
                        bg="#e0f2f1"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard
                        icon={<Repeat />}
                        label="Repeat Customers (All Time)"
                        value={data.repeatCustomersAllTime}
                        color="#4527a0"
                        bg="#ede7f6"
                      />
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  No data available for this city.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────
const CityAnalytics = () => {
  const [cities, setCities] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCities(); }, []);

  const fetchCities = async () => {
    setLoading(true);
    const res = await getRequest(`${server.uri}cities`);
    if (res) { setCities(res); setFiltered(res); }
    setLoading(false);
  };

  const handleSearch = (e) => {
    const val = e.target.value.toLowerCase();
    setSearch(val);
    setFiltered(cities.filter((c) => c.name.toLowerCase().includes(val)));
  };

  return (
    <Box sx={{ p: 3, mt: 6 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="h4" fontWeight="bold">City-wise Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            Click any city to see partner stats, bookings &amp; repeat customers.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchCities}><Refresh /></IconButton>
        </Tooltip>
      </Box>

      <TextField
        variant="outlined"
        placeholder="Search city..."
        value={search}
        onChange={handleSearch}
        size="small"
        sx={{ mt: 2, mb: 3, width: 320 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
        }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <Paper elevation={2}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
                <TableRow>
                  <TableCell><strong>City Name</strong></TableCell>
                  <TableCell><strong>Coverage</strong></TableCell>
                  <TableCell align="right"><strong>Analytics</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>No cities found.</TableCell>
                  </TableRow>
                ) : (
                  filtered.map((city) => <CityRow key={city.cityId} city={city} />)
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default CityAnalytics;
